import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Spinner } from "reactstrap";
import { CART_COLUMNS, cartCellLabel } from "./columns";
import { useTable } from "react-table";
import { connect } from "react-redux";
import axios from "axios";
import { emptyAuthCart, getAuthPoints } from "@/containers/App/auth";
import { setPointBalance } from '@/redux/actions/balanceActions';
import { clearCart } from '@/redux/actions/cartActions';
import {
  useDispatch,
  sendFlashMessage,
  ApiErrorMessage,
} from "@/shared/components/flash";
import TemplateButton from "@/shared/components/TemplateButton";
import { useTranslation } from "react-i18next";
import TopUpModal from "./TopUpModal";
import VisaRedemptionModal from "./VisaRedemptionModal";
import ShortfallWarningModal from "./ShortfallWarningModal";
import {
  getCartTotalPointsOrDollars,
  getRedemptionValue,
  DEFAULT_CURRENCY,
  isForeignCurrency,
} from "@/shared/helpers";
import { getMerchant } from "@/services/program/getMerchant";

import { gcmCartBusiness } from "@/containers/Participant/GcmShared/helpers";
const API_STORAGE_URL = `${process.env.REACT_APP_API_STORAGE_URL}`;
const VISA_MERCHANT_NAME_PATTERN = /visa/i;
const GIFT_CODES_REDIRECT_URL = "/participant/my-gift-codes";

const CheckoutPage = ({
  cart,
  program,
  pointBalance,
  organization,
  dispatch,
  currency,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const flashDispatch = useDispatch();
  const [isLoading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [totalPoints, setTotalPoints] = useState(0);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [visaRedemptions, setVisaRedemptions] = useState(null);
  const [warningOpen, setWarningOpen] = useState(false);

  useEffect(() => {
    if (!cart?.items?.length || !program?.id || !program?.organization_id) {
      setTableData([]);
      setTotalPoints(0);
      return;
    }

    (async () => {
      const results = await Promise.all(
        cart.items.map(async (item) => {
          const merchant = await getMerchant(
            program.organization_id,
            program.id,
            item.merchant_id
          );
          let redemptionValue = getRedemptionValue(item, merchant.pivot);
          let giftCode = `$${parseFloat(item.sku_value, 3).toFixed(2)} ${t(
            "gift_code"
          )} = ${Math.ceil(redemptionValue * program.factor_valuation)}`;

          if (isForeignCurrency(currency)) {
            // redemptionValue = parseFloat(redemptionValue * currency.conversion_rate).toFixed(2)
            redemptionValue = parseFloat(redemptionValue).toFixed(2);
            giftCode = `${currency.symbol}${parseFloat(item.sku_value).toFixed(
              2
            )} ${t("gift_code")} = ${Math.ceil(
              (1 / currency.conversion_rate) *
                redemptionValue *
                program.factor_valuation
            )}`;
          }

          return {
            redemptionValue: isForeignCurrency(currency)
              ? (1 / currency.conversion_rate) * redemptionValue
              : redemptionValue,
            rowData: {
              logo: (() => {
                if (String(item.merchant_id).startsWith('gcm-')) return gcmCartBusiness(item);
                return `${API_STORAGE_URL}/${item.merchant_icon}`;
              })(),
              // Gift Card Market requires its product name format
              name: String(item.merchant_id).startsWith('gcm-')
                ? t('gcm_local_gift_card', { name: item.merchant_name })
                : item.merchant_name,
              giftCode: giftCode,
              quantity: item.qty,
              price: parseFloat(
                (isForeignCurrency(currency)
                  ? (1 / currency.conversion_rate) * redemptionValue
                  : redemptionValue) * program.factor_valuation
              ).toFixed(0),
              total:
                parseFloat(
                  (isForeignCurrency(currency)
                    ? (1 / currency.conversion_rate) * redemptionValue
                    : redemptionValue) * program.factor_valuation
                ).toFixed(0) * item.qty,
            },
          };
        })
      );

      const redemptionValues = results.map((result) => result.redemptionValue);
      const nextTableData = results.map((result) => result.rowData);

      setTableData(nextTableData);
      const totalPointsValue = getCartTotalPointsOrDollars(
        cart?.items,
        program.factor_valuation,
        redemptionValues
      );
      setTotalPoints(Math.ceil(totalPointsValue));
    })();
  }, [cart, program, currency, t]);

  // Set just before our own post-checkout redirect so the leave-page guard below
  // doesn't prompt for a navigation the page itself started
  const redirectingRef = useRef(false);
  const redirectToGiftCodes = () => {
    redirectingRef.current = true;
    window.location.href = GIFT_CODES_REDIRECT_URL;
  };

  useEffect(() => {
    if (!isLoading) return;
    const handler = (e) => {
      if (redirectingRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isLoading]);

  const confirmOrder = (topupData = null) => {
    setLoading(true);
    const payload = { ...(cart ?? { items: [] }) };

    if (currency?.value && currency.value !== DEFAULT_CURRENCY.value) {
      payload.currency_code = currency.value;
    }

    // if (topupData) {
    //   payload.topup_reference_number  = topupData.reference_number ?? null;
    //   payload.topup_journal_event_id  = topupData.topup_journal_event_id ?? null;
    //   payload.topup_event_xml_data_id = topupData.topup_event_xml_data_id ?? null;
    //   payload.topup_amount            = topupData.topup_amount ?? null;
    // }

    axios
      .post(
        `/organization/${organization.id}/program/${program.id}/checkout`,
        payload
      )
      .then((res) => {
        if (res.status === 200) {
          if (res.data?.success) {
            getAuthPoints().then((updatedBalance) => {
              dispatch(setPointBalance(updatedBalance));
              dispatch(clearCart());
              emptyAuthCart();

              const visaCodes = (res.data.gift_codes_redeemed_for ?? [])
                .filter(
                  (item) =>
                    VISA_MERCHANT_NAME_PATTERN.test(item.merchant?.name ?? "") &&
                    item.tango_reference_order_id
                )
                .map((item) => ({
                  merchantName: item.merchant?.name,
                  raNumber: item.tango_reference_order_id,
                }));

              if (visaCodes.length) {
                setLoading(false);
                setVisaRedemptions(visaCodes);
              } else {
                redirectToGiftCodes();
              }
            });
            return;
          }
        }

        setLoading(false);
      })
      .catch((err) => {
        flashDispatch(
          sendFlashMessage(
            <ApiErrorMessage errors={err.response.data} />,
            "alert-danger",
            "top"
          )
        );
        setLoading(false);
      });
  };

  const shortfallPts = Math.max(0, totalPoints - Math.floor(pointBalance?.pointBalance ?? 0));
  const shortfallDollars = shortfallPts > 0
    ? parseFloat((shortfallPts / program.factor_valuation).toFixed(2))
    : 0;
  // A shortfall can only be covered via the credit-card top-up modal below — when the
  // program has credit card payments disabled, there is no way to complete this order.
  const ccTopUpBlocked = shortfallPts > 0 && !program.allow_credit_card_payments;

  const onConfirmClick = () => {
    if (ccTopUpBlocked) return;
    if (shortfallPts > 0) {
      setWarningOpen(true);
    } else {
      confirmOrder();
    }
  };

  const onEditCart = () => {
    setWarningOpen(false);
    navigate("/participant/cart");
  };

  const onPurchasePoints = () => {
    setWarningOpen(false);
    setTopUpOpen(true);
  };

  const onTopUpSuccess = (resp) => {
    getAuthPoints().then((updatedBalance) => {
      dispatch(setPointBalance(updatedBalance));
      setTopUpOpen(false);
      confirmOrder(resp);
    });
  };

  const columns = React.useMemo(() => CART_COLUMNS, []);

  const { getTableProps, headerGroups, rows, prepareRow } = useTable({
    columns,
    data: tableData,
  });

  const columnCount = headerGroups[0]?.headers?.length ?? columns.length;
  const balanceAfterPurchase = Math.ceil((pointBalance?.pointBalance ?? 0) - totalPoints);
  const hasItems = Boolean(cart?.items?.length);

  if (!hasItems && !isLoading && !visaRedemptions) {
    return (
      <section className="co-panel">
        <div className="rc-panel__empty">{t("cart_empty")}</div>
      </section>
    );
  }

  return (
    <section className="co-panel">
      {isLoading && (
        <div className="co-panel__processing-overlay">
          <div className="co-panel__processing-box">
            <Spinner color="primary" />
            <p>Processing your order… please do not close or refresh this page.</p>
          </div>
        </div>
      )}
      <div className="co-panel__intro">
        <h2 className="co-panel__title">
          {t("checkout")}: {t("confirm_your_order")}
        </h2>
        <p className="co-panel__copy">
          {t("order_desc_1")}{" "}
          <strong>
            {totalPoints.toLocaleString()} {t("points")}
          </strong>{" "}
          {t("order_desc_2")}
        </p>
      </div>

      <div className="co-panel__notice">
        <strong>{t("order_desc_3")}</strong>
      </div>

      <p className="co-panel__helper">{t("order_desc_4")}</p>

      <div className="rc-panel__table-wrap">
        <Table responsive size="md" className="rc-panel__table co-panel__table table--stack-mobile" {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps();

              return (
                <tr key={key} {...headerGroupProps}>
                  {headerGroup.headers.map((column) => {
                    const { key: headerKey, ...headerProps } = column.getHeaderProps();

                    return (
                      <th key={headerKey} {...headerProps}>
                        {t(column.Header)}
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody>
            {rows.map((row) => {
              prepareRow(row);
              const { key, ...rowProps } = row.getRowProps();

              return (
                <tr key={key} {...rowProps}>
                  {row.cells.map((cell) => {
                    const { key: cellKey, ...cellProps } = cell.getCellProps();

                    return (
                      <td
                        key={cellKey}
                        className={cell.column.id === "logo" ? "cart_item--logo" : undefined}
                        {...cellProps}
                        data-label={cartCellLabel(cell.column, t)}
                      >
                        {cell.render("Cell")}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      <div className="rc-panel__footer co-panel__footer">
        <div className="rc-panel__summary">
          <div className="rc-panel__summary-item">
            <span className="rc-panel__summary-label">{t("total")}</span>
            <strong className="rc-panel__summary-value">
              {totalPoints.toLocaleString()} {t("points")}
            </strong>
          </div>
          {shortfallPts > 0 ? (
            <div className="rc-panel__summary-item rc-panel__summary-item--topup">
              <span className="rc-panel__summary-label">Card top-up required</span>
              <strong className="rc-panel__summary-value">
                ${shortfallDollars.toFixed(2)} ({shortfallPts.toLocaleString()} pts)
              </strong>
            </div>
          ) : (
            <div className="rc-panel__summary-item">
              <span className="rc-panel__summary-label">{t("balance_after_purchase")}</span>
              <strong className="rc-panel__summary-value">
                {balanceAfterPurchase.toLocaleString()} {t("points")}
              </strong>
            </div>
          )}
        </div>

        <div className="rc-panel__actions" style={ccTopUpBlocked ? { alignSelf: "flex-end" } : undefined}>
          {ccTopUpBlocked ? (
            <div className="co-panel__notice" style={{ marginBottom: 0 }}>
              <strong>{t("credit_card_disabled_message")}</strong>
            </div>
          ) : (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <TemplateButton
                type="submit"
                disabled={isLoading}
                spinner={isLoading}
                onClick={onConfirmClick}
                text={t("confirm_my_order")}
              />
            </div>
          )}
        </div>
      </div>

      <ShortfallWarningModal
        isOpen={warningOpen}
        toggle={() => setWarningOpen(false)}
        shortfallPoints={shortfallPts}
        shortfallDollars={shortfallDollars}
        onEditCart={onEditCart}
        onPurchasePoints={onPurchasePoints}
      />

      <TopUpModal
        isOpen={topUpOpen}
        toggle={() => { setTopUpOpen(false); setLoading(false); }}
        shortfallDollars={shortfallDollars}
        shortfallPoints={shortfallPts}
        onSuccess={onTopUpSuccess}
        onProcessingStart={() => setLoading(true)}
        orgId={organization.id}
        programId={program.id}
      />

      <VisaRedemptionModal
        isOpen={Boolean(visaRedemptions)}
        redemptions={visaRedemptions ?? []}
        onClose={redirectToGiftCodes}
      />
    </section>
  );
};

const mapStateToProps = (state) => {
  return {
    cart: state.cart,
    program: state.program,
    pointBalance: state.pointBalance,
    organization: state.organization,
    currency: state.currency,
  };
};

const mapDispatchToProps = (dispatch) => ({
  dispatch,
});

export default connect(mapStateToProps, mapDispatchToProps)(CheckoutPage);
